import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  type ReactNode,
  Suspense,
} from "react";
import { useLocation, useNavigate, useRoutes } from "react-router-dom";
import { appRoutes } from "@/appRoutes";
import { type LucideIcon } from "lucide-react";
import { workspaceService } from "@/services/workspace";
import {
  buildWorkspaceFallbackLabel,
  closeAllNonPinnedTabs,
  createEmptyWorkspaceTabsByModule,
  DEFAULT_WORKSPACE_TAB,
  getWorkspaceModuleIdForPath,
  inferWorkspaceTabMetadata,
  migrateStoredTabs,
  WORKSPACE_ACTIVE_TAB_BY_MODULE_KEY,
  WORKSPACE_ACTIVE_TAB_KEY,
  normalizeWorkspaceRoutePath,
  WORKSPACE_TABS_BY_MODULE_KEY,
  WORKSPACE_TABS_KEY,
  type WorkspaceActiveTabByModule,
  type WorkspaceModuleId,
  type WorkspaceTabRecord,
  type WorkspaceTabsByModule,
} from "@/contexts/workspaceState";

export type Tab = WorkspaceTabRecord;
export type OpenTabOptions = Partial<Pick<WorkspaceTabRecord, "pinned" | "kind" | "restorationKey" | "updatedAt">>;

interface WorkspaceContextType {
  tabs: WorkspaceTabRecord[];
  tabsByModule: WorkspaceTabsByModule;
  visibleTabs: WorkspaceTabRecord[];
  activeTabId: string | null;
  activeTabIdByModule: WorkspaceActiveTabByModule;
  activeModuleId: WorkspaceModuleId;
  isEnabled: boolean;
  openTab: (path: string, label: string, icon?: LucideIcon, options?: OpenTabOptions) => void;
  closeTab: (id: string) => void;
  closeAllTabs: () => void;
  pinTab: (id: string) => void;
  unpinTab: (id: string) => void;
  setActiveTabId: (id: string) => void;
  getVisibleTabs: () => WorkspaceTabRecord[];
  getActiveModuleId: () => WorkspaceModuleId;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

const DEFAULT_TAB: WorkspaceTabRecord = DEFAULT_WORKSPACE_TAB;
const WORKSPACE_MODULE_IDS: WorkspaceModuleId[] = [
  "geral",
  "comercial",
  "marcas",
  "patentes",
  "di",
  "software",
  "autoral",
  "operacional",
  "internacional",
  "admin",
];

const FALLBACK_WORKSPACE_CONTEXT: WorkspaceContextType = {
  tabs: [DEFAULT_TAB],
  tabsByModule: createEmptyWorkspaceTabsByModule(),
  visibleTabs: [DEFAULT_TAB],
  activeTabId: DEFAULT_TAB.id,
  activeTabIdByModule: { geral: DEFAULT_TAB.id },
  activeModuleId: "geral",
  isEnabled: true,
  openTab: () => {},
  closeTab: () => {},
  closeAllTabs: () => {},
  pinTab: () => {},
  unpinTab: () => {},
  setActiveTabId: () => {},
  getVisibleTabs: () => [DEFAULT_TAB],
  getActiveModuleId: () => "geral",
};

function serializeWorkspaceTab(tab: WorkspaceTabRecord) {
  const { id, path, label, pinned, kind, restorationKey, updatedAt } = tab;
  return { id, path, label, pinned, kind, restorationKey, updatedAt };
}

function createWorkspaceTab(
  path: string,
  label: string,
  icon?: LucideIcon,
  options?: OpenTabOptions,
): WorkspaceTabRecord {
  const normalizedPath = normalizeWorkspaceRoutePath(path);
  const inferredMetadata = inferWorkspaceTabMetadata(normalizedPath);

  return {
    id: normalizedPath,
    path: normalizedPath,
    label,
    icon,
    pinned: options?.pinned ?? false,
    kind: options?.kind ?? inferredMetadata.kind,
    restorationKey: options?.restorationKey ?? inferredMetadata.restorationKey,
    updatedAt: options?.updatedAt ?? new Date().toISOString(),
  };
}

function flattenTabsByModule(tabsByModule: WorkspaceTabsByModule) {
  return WORKSPACE_MODULE_IDS.flatMap((moduleId) => tabsByModule[moduleId] ?? []);
}

function sanitizeTabsByModule(value: unknown): WorkspaceTabsByModule {
  const nextTabsByModule = createEmptyWorkspaceTabsByModule();

  if (!value || typeof value !== "object") {
    return nextTabsByModule;
  }

  const candidate = value as Record<string, unknown>;
  const seenTabIds = new Set<string>(nextTabsByModule.geral.map((tab) => tab.id));

  for (const moduleId of WORKSPACE_MODULE_IDS) {
    const rawTabs = candidate[moduleId];
    if (!Array.isArray(rawTabs) || rawTabs.length === 0) {
      continue;
    }

    const sanitizedTabs = migrateStoredTabs(rawTabs);
    for (const tab of sanitizedTabs) {
      const ownerModuleId = getWorkspaceModuleIdForPath(tab.path);
      if (seenTabIds.has(tab.id)) {
        continue;
      }

      seenTabIds.add(tab.id);
      nextTabsByModule[ownerModuleId] = [...nextTabsByModule[ownerModuleId], tab];
    }
  }

  if (nextTabsByModule.geral.length === 0) {
    nextTabsByModule.geral = [DEFAULT_TAB];
  }

  return nextTabsByModule;
}

function sanitizeActiveTabIdByModule(
  value: unknown,
  tabsByModule: WorkspaceTabsByModule,
): WorkspaceActiveTabByModule {
  const nextActiveTabIdByModule: WorkspaceActiveTabByModule = {};
  const candidate = value && typeof value === "object" ? (value as Record<string, unknown>) : {};

  for (const moduleId of WORKSPACE_MODULE_IDS) {
    const tabs = tabsByModule[moduleId] ?? [];
    const rawActiveTabId = typeof candidate[moduleId] === "string" ? candidate[moduleId].trim() : null;
    const normalizedActiveTabId = rawActiveTabId ? normalizeWorkspaceRoutePath(rawActiveTabId) : null;

    if (normalizedActiveTabId && tabs.some((tab) => tab.id === normalizedActiveTabId)) {
      nextActiveTabIdByModule[moduleId] = normalizedActiveTabId;
      continue;
    }

    nextActiveTabIdByModule[moduleId] = tabs[0]?.id ?? null;
  }

  return nextActiveTabIdByModule;
}

function migrateLegacyWorkspaceState(
  legacyTabs: WorkspaceTabRecord[],
  legacyActiveTabId: string | null,
): {
  tabsByModule: WorkspaceTabsByModule;
  activeTabIdByModule: WorkspaceActiveTabByModule;
} {
  const nextTabsByModule = createEmptyWorkspaceTabsByModule();
  const nextActiveTabIdByModule: WorkspaceActiveTabByModule = {};
  const seenTabIds = new Set<string>(nextTabsByModule.geral.map((tab) => tab.id));
  const sanitizedLegacyTabs = legacyTabs.length > 0 ? migrateStoredTabs(legacyTabs) : [DEFAULT_TAB];
  const normalizedLegacyActiveTabId = legacyActiveTabId ? normalizeWorkspaceRoutePath(legacyActiveTabId) : null;

  for (const tab of sanitizedLegacyTabs) {
    const moduleId = getWorkspaceModuleIdForPath(tab.path);
    if (seenTabIds.has(tab.id)) {
      continue;
    }

    seenTabIds.add(tab.id);
    nextTabsByModule[moduleId] = [...nextTabsByModule[moduleId], tab];

    if (tab.id === normalizedLegacyActiveTabId) {
      nextActiveTabIdByModule[moduleId] = tab.id;
    }
  }

  for (const moduleId of WORKSPACE_MODULE_IDS) {
    if (!nextActiveTabIdByModule[moduleId]) {
      nextActiveTabIdByModule[moduleId] = nextTabsByModule[moduleId][0]?.id ?? null;
    }
  }

  return {
    tabsByModule: nextTabsByModule,
    activeTabIdByModule: nextActiveTabIdByModule,
  };
}

function getStoredWorkspaceState() {
  const savedTabsByModule = localStorage.getItem(WORKSPACE_TABS_BY_MODULE_KEY);
  const savedActiveTabIdByModule = localStorage.getItem(WORKSPACE_ACTIVE_TAB_BY_MODULE_KEY);

  if (savedTabsByModule) {
    try {
      const tabsByModule = sanitizeTabsByModule(JSON.parse(savedTabsByModule));
      const activeTabIdByModule = sanitizeActiveTabIdByModule(
        savedActiveTabIdByModule ? JSON.parse(savedActiveTabIdByModule) : null,
        tabsByModule,
      );
      return { tabsByModule, activeTabIdByModule };
    } catch {
      localStorage.removeItem(WORKSPACE_TABS_BY_MODULE_KEY);
      localStorage.removeItem(WORKSPACE_ACTIVE_TAB_BY_MODULE_KEY);
    }
  }

  const savedLegacyTabs = localStorage.getItem(WORKSPACE_TABS_KEY);
  if (!savedLegacyTabs) {
    const tabsByModule = createEmptyWorkspaceTabsByModule();
    return {
      tabsByModule,
      activeTabIdByModule: sanitizeActiveTabIdByModule(null, tabsByModule),
    };
  }

  try {
    return migrateLegacyWorkspaceState(
      migrateStoredTabs(JSON.parse(savedLegacyTabs)),
      localStorage.getItem(WORKSPACE_ACTIVE_TAB_KEY),
    );
  } catch {
    localStorage.removeItem(WORKSPACE_TABS_KEY);
    localStorage.removeItem(WORKSPACE_ACTIVE_TAB_KEY);
    const tabsByModule = createEmptyWorkspaceTabsByModule();
    return {
      tabsByModule,
      activeTabIdByModule: sanitizeActiveTabIdByModule(null, tabsByModule),
    };
  }
}

function closeAllTabsForModule(
  moduleId: WorkspaceModuleId,
  tabs: WorkspaceTabRecord[],
  activeTabId: string | null,
) {
  if (tabs.length === 0) {
    return {
      tabs,
      activeTabId: null,
    };
  }

  const next = closeAllNonPinnedTabs(tabs, activeTabId);
  if (moduleId === "geral") {
    return next;
  }

  const filteredTabs = next.tabs.filter((tab) => tab.id !== DEFAULT_TAB.id);
  return {
    tabs: filteredTabs,
    activeTabId: filteredTabs[0]?.id ?? null,
  };
}

export const WorkspaceProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isClosingAllTabsRef = useRef(false);
  const pendingCloseAllRef = useRef<
    | {
        moduleId: WorkspaceModuleId;
        tabs: WorkspaceTabRecord[];
        activeTabId: string | null;
      }
    | null
  >(null);
  const initialWorkspaceState = useMemo(() => getStoredWorkspaceState(), []);
  const [tabsByModule, setTabsByModule] = useState<WorkspaceTabsByModule>(initialWorkspaceState.tabsByModule);
  const [activeTabIdByModule, setActiveTabIdByModuleState] = useState<WorkspaceActiveTabByModule>(
    initialWorkspaceState.activeTabIdByModule,
  );
  const activeTabIdByModuleRef = useRef<WorkspaceActiveTabByModule>(initialWorkspaceState.activeTabIdByModule);

  const [isInitialized, setIsInitialized] = useState(false);

  const activeModuleId = getWorkspaceModuleIdForPath(location.pathname);
  const visibleTabs = tabsByModule[activeModuleId] ?? [];
  const activeTabId = activeTabIdByModule[activeModuleId] ?? visibleTabs[0]?.id ?? null;
  const tabs = useMemo(() => flattenTabsByModule(tabsByModule), [tabsByModule]);

  const applyActiveTabIdByModule = useCallback(
    (updater: WorkspaceActiveTabByModule | ((prev: WorkspaceActiveTabByModule) => WorkspaceActiveTabByModule)) => {
      setActiveTabIdByModuleState((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        activeTabIdByModuleRef.current = next;
        return next;
      });
    },
    [],
  );

  const updateModuleTabs = useCallback(
    (moduleId: WorkspaceModuleId, updater: (tabs: WorkspaceTabRecord[]) => WorkspaceTabRecord[]) => {
      setTabsByModule((prev) => ({
        ...prev,
        [moduleId]: updater(prev[moduleId] ?? []),
      }));
    },
    [],
  );

  // Sync state to API & localStorage
  useEffect(() => {
    const persistedTabsByModule = Object.fromEntries(
      WORKSPACE_MODULE_IDS.map((moduleId) => [
        moduleId,
        (tabsByModule[moduleId] ?? []).map(serializeWorkspaceTab),
      ]),
    );
    localStorage.setItem(WORKSPACE_TABS_BY_MODULE_KEY, JSON.stringify(persistedTabsByModule));
    localStorage.removeItem(WORKSPACE_TABS_KEY);
  }, [tabsByModule]);

  useEffect(() => {
    localStorage.setItem(WORKSPACE_ACTIVE_TAB_BY_MODULE_KEY, JSON.stringify(activeTabIdByModule));
    localStorage.removeItem(WORKSPACE_ACTIVE_TAB_KEY);
  }, [activeTabIdByModule]);

  useEffect(() => {
    activeTabIdByModuleRef.current = activeTabIdByModule;
  }, [activeTabIdByModule]);

  useEffect(() => {
    if (!isInitialized) return;

    // Sync to remote API whenever it changes, using a simple fire-and-forget
    const timeoutId = setTimeout(() => {
      workspaceService
        .saveWorkspaceTabs({ tabsByModule, activeTabIdByModule })
        .catch(console.error);
    }, 1000); // 1s debounce

    return () => clearTimeout(timeoutId);
  }, [tabsByModule, activeTabIdByModule, isInitialized]);

  // Initial fetch from remote
  useEffect(() => {
    let mounted = true;

    workspaceService
      .getWorkspaceTabs()
      .then((data) => {
        if (mounted) {
          if (data) {
            const sanitizedTabsByModule = sanitizeTabsByModule(data.tabsByModule);
            setTabsByModule(sanitizedTabsByModule);
            applyActiveTabIdByModule(
              sanitizeActiveTabIdByModule(data.activeTabIdByModule, sanitizedTabsByModule),
            );
          }
          setIsInitialized(true);
        }
      })
      .catch((err) => {
        console.error("Failed to load workspace tabs", err);
        if (mounted) setIsInitialized(true);
      });

    return () => {
      mounted = false;
    };
  }, [applyActiveTabIdByModule]);

  const openTab = (path: string, label: string, icon?: LucideIcon, options?: OpenTabOptions) => {
    const normalizedPath = normalizeWorkspaceRoutePath(path);
    const moduleId = getWorkspaceModuleIdForPath(normalizedPath);

    updateModuleTabs(moduleId, (prev) => {
      const existing = prev.find((tab) => tab.id === normalizedPath);
      if (existing) return prev;
      return [...prev, createWorkspaceTab(normalizedPath, label, icon, options)];
    });

    applyActiveTabIdByModule((prev) => ({
      ...prev,
      [moduleId]: normalizedPath,
    }));
    navigate(normalizedPath);
  };

  const closeTab = (id: string) => {
    const normalizedId = normalizeWorkspaceRoutePath(id);
    const moduleId = getWorkspaceModuleIdForPath(normalizedId);
    const moduleTabs = tabsByModule[moduleId] ?? [];
    const newTabs =
      moduleId === "geral"
        ? moduleTabs.filter((tab) => tab.id !== normalizedId) || [DEFAULT_TAB]
        : moduleTabs.filter((tab) => tab.id !== normalizedId);
    const ensuredTabs = moduleId === "geral" && newTabs.length === 0 ? [DEFAULT_TAB] : newTabs;
    const nextActiveTabId =
      activeTabIdByModule[moduleId] === normalizedId
        ? ensuredTabs[ensuredTabs.length - 1]?.id ?? null
        : activeTabIdByModule[moduleId] ?? ensuredTabs[0]?.id ?? null;

    updateModuleTabs(moduleId, () => ensuredTabs);
    applyActiveTabIdByModule((prev) => ({
      ...prev,
      [moduleId]: nextActiveTabId,
    }));

    if (activeModuleId !== moduleId) {
      return;
    }

    if (nextActiveTabId) {
      const nextTab = ensuredTabs.find((tab) => tab.id === nextActiveTabId);
      if (nextTab) {
        navigate(nextTab.path);
        return;
      }
    }

    navigate("/geral");
  };

  const closeAllTabs = () => {
    const moduleId = activeModuleId;

    updateModuleTabs(moduleId, (prev) => {
      const next = closeAllTabsForModule(moduleId, prev, activeTabIdByModuleRef.current[moduleId] ?? null);
      pendingCloseAllRef.current = { moduleId, ...next };
      isClosingAllTabsRef.current = true;
      return next.tabs;
    });
  };

  const pinTab = (id: string) => {
    const moduleId = getWorkspaceModuleIdForPath(id);

    updateModuleTabs(moduleId, (prev) =>
      prev.map((tab) =>
        tab.id === id
          ? {
              ...tab,
              pinned: true,
              updatedAt: new Date().toISOString(),
            }
          : tab,
      ),
    );
  };

  const unpinTab = (id: string) => {
    const moduleId = getWorkspaceModuleIdForPath(id);

    updateModuleTabs(moduleId, (prev) =>
      prev.map((tab) =>
        tab.id === id
          ? {
              ...tab,
              pinned: false,
              updatedAt: new Date().toISOString(),
            }
          : tab,
      ),
    );
  };

  const setActiveTabId = (id: string) => {
    const normalizedId = normalizeWorkspaceRoutePath(id);
    const moduleId = getWorkspaceModuleIdForPath(normalizedId);
    const tab = (tabsByModule[moduleId] ?? []).find((entry) => entry.id === normalizedId);
    if (tab) {
      applyActiveTabIdByModule((prev) => ({
        ...prev,
        [moduleId]: tab.id,
      }));
      navigate(tab.path);
    }
  };

  useEffect(() => {
    const pending = pendingCloseAllRef.current;
    if (!pending) return;

    pendingCloseAllRef.current = null;
    applyActiveTabIdByModule((prev) => ({
      ...prev,
      [pending.moduleId]: pending.activeTabId,
    }));
    navigate(pending.tabs[0]?.path ?? DEFAULT_TAB.path);
  }, [applyActiveTabIdByModule, navigate, tabsByModule]);

  // Keep active tab in sync with browser navigation (back/forward)
  useEffect(() => {
    const normalizedPath = normalizeWorkspaceRoutePath(location.pathname);
    const moduleId = getWorkspaceModuleIdForPath(normalizedPath);
    const moduleTabs = tabsByModule[moduleId] ?? [];
    const moduleActiveTabId = activeTabIdByModule[moduleId] ?? moduleTabs[0]?.id ?? null;

    if (isClosingAllTabsRef.current) {
      if (normalizedPath === moduleActiveTabId) {
        isClosingAllTabsRef.current = false;
      }
      return;
    }

    if (normalizedPath !== moduleActiveTabId) {
      // Find a matching route title
      const existingTab = moduleTabs.find((tab) => tab.id === normalizedPath);
      if (existingTab) {
        applyActiveTabIdByModule((prev) => ({
          ...prev,
          [moduleId]: existingTab.id,
        }));
      } else {
        // If navigating to a new route not in tabs (e.g. directly in URL), try to open it
        // We might not have a perfect label, so we use a fallback or try to infer it.
        const fallbackLabel = buildWorkspaceFallbackLabel(normalizedPath);
        updateModuleTabs(moduleId, (prev) => {
          if (prev.some((tab) => tab.id === normalizedPath)) return prev;
          return [...prev, createWorkspaceTab(normalizedPath, fallbackLabel)];
        });
        applyActiveTabIdByModule((prev) => ({
          ...prev,
          [moduleId]: normalizedPath,
        }));
      }
    }
  }, [
    activeTabIdByModule,
    applyActiveTabIdByModule,
    location.pathname,
    tabsByModule,
    updateModuleTabs,
  ]);

  return (
    <WorkspaceContext.Provider
      value={{
        tabs,
        tabsByModule,
        visibleTabs,
        activeTabId,
        activeTabIdByModule,
        activeModuleId,
        isEnabled: true,
        openTab,
        closeTab,
        closeAllTabs,
        pinTab,
        unpinTab,
        setActiveTabId,
        getVisibleTabs: () => visibleTabs,
        getActiveModuleId: () => activeModuleId,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    console.error("Workspace context unavailable; using fallback workspace state");
    return FALLBACK_WORKSPACE_CONTEXT;
  }
  return context;
};

export const WorkspaceTabRenderer = () => {
  const routeElement = useRoutes(appRoutes);

  return (
    <Suspense fallback={<div className="flex h-full items-center justify-center"><div className="w-8 h-8 border-4 border-slate-300 border-t-primary rounded-full animate-spin"></div></div>}>
      {routeElement}
    </Suspense>
  );
};
